// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Slope conversions and reference checks — all pure, all unit-tested. An angle
 * in degrees is the one true value; everything else (percent grade, roof pitch
 * in rise-per-12, the 1:n ratio, and the accessibility-ramp verdict) is derived
 * from it. These are the numbers a roofer, a solar installer or an access
 * auditor actually wants to write down.
 */

const DEG = 180 / Math.PI;
const RAD = Math.PI / 180;

/**
 * The steepest angle the tan-based conversions still mean anything at. Past it
 * they diverge — a phone a hair off plumb already gives a six-figure grade and
 * an eighteen-digit roof pitch, which no fixed-width readout can hold. The
 * photo protractor already refuses to convert near vertical; this is the same
 * guard for the live sensor.
 */
export const MAX_CONV_DEG = 85;

/** Whether a slope is shallow enough for grade/pitch/ratio to be meaningful. */
export function isConvertible(deg: number): boolean {
  return Number.isFinite(deg) && Math.abs(deg) <= MAX_CONV_DEG;
}

/** Percent grade (rise/run × 100) from an angle. 45° → 100 %. */
export function degToGrade(deg: number): number {
  return Math.tan(deg * RAD) * 100;
}

/** Angle from a percent grade. 100 % → 45°. */
export function gradeToDeg(grade: number): number {
  return Math.atan(grade / 100) * DEG;
}

/** Rise per 12 units of run — the number in a "5:12" roof pitch. */
export function degToRoofRise(deg: number): number {
  return Math.tan(deg * RAD) * 12;
}

/** A roof-pitch label like "5:12", rounded to the nearest common increment. */
export function roofPitchLabel(deg: number): string {
  if (!isConvertible(deg)) return '—';
  const rise = degToRoofRise(deg);
  // Roofs are quoted in whole or half units of rise per 12.
  const rounded = Math.round(rise * 2) / 2;
  const shown = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${shown}:12`;
}

/**
 * The run-per-rise ratio for a slope, as the `n` in "1:n". A shallow 1:12 ramp
 * (≈4.76°) → 12. Returns Infinity for a dead-flat 0°.
 */
export function degToRunRatio(deg: number): number {
  const t = Math.tan(Math.abs(deg) * RAD);
  return t < 1e-9 ? Infinity : 1 / t;
}

/** A "1:n" ratio label. */
export function ratioLabel(deg: number): string {
  if (!isConvertible(deg)) return '—';
  const n = degToRunRatio(deg);
  if (!Number.isFinite(n)) return 'flat';
  return `1:${n >= 10 ? Math.round(n) : n.toFixed(1)}`;
}

export type RampLevel = 'flat' | 'gentle' | 'preferred' | 'max' | 'steep';

export interface RampVerdict {
  level: RampLevel;
  /** A short human label. */
  label: string;
  /** The 1:n ratio for the measured slope. */
  ratio: string;
}

/**
 * Classify a slope against widely-used accessibility-ramp thresholds. This is a
 * convenience reference for common guidance — NOT legal or code advice, and the
 * UI says so. Thresholds (rise:run → angle):
 *   1:20 ≈ 2.86°  gentle (often allowed without handrails)
 *   1:14 ≈ 4.09°  preferred accessible gradient (e.g. AS 1428)
 *   1:12 ≈ 4.76°  common maximum accessible gradient (e.g. ADA)
 *   steeper       exceeds the common 1:12 maximum
 */
export function rampVerdict(deg: number): RampVerdict {
  const a = Math.abs(deg);
  const ratio = ratioLabel(a);
  if (a < 0.15) return { level: 'flat', label: 'Essentially flat', ratio };
  if (a <= gradeToDeg(5) + 1e-9) return { level: 'gentle', label: 'Gentle — within 1:20', ratio };
  if (a <= 4.09 + 1e-9) return { level: 'preferred', label: 'Within the 1:14 preferred gradient', ratio };
  if (a <= 4.764 + 1e-9) return { level: 'max', label: 'Within the 1:12 maximum', ratio };
  return { level: 'steep', label: 'Steeper than the common 1:12 maximum', ratio };
}
